// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  CreateExternalUserParameters,
  NotificationConfigurationInput,
  UpdateExternalUserParameters,
  createExternalUser,
  updateExternalUser,
} from '@/account/api/external-users';
import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { handleAPIError } from '@/api/error-utils';
import { FormTextField } from '@/components/form-text-field';
import { ScrollIndicator } from '@/components/scroll-indicator';
import {
  MESSAGE_TYPES,
  MESSAGE_TYPE_DESCRIPTIONS,
  MESSAGE_TYPE_LABELS,
} from '@/constants/message-type';
import { NOTIFICATION_CHANNELS } from '@/constants/notification-channel';
import { useMutationWithGlobalErrorHandler } from '@/hooks/use-mutation-with-global-error-handler';
import { useScrollIndicator } from '@/hooks/use-scroll-indicator';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
  notifications: z.array(
    z.object({
      messageType: z.enum(MESSAGE_TYPES),
      activeChannels: z.array(z.enum(NOTIFICATION_CHANNELS)),
    })
  ),
});

type ExternalUserFormData = z.infer<typeof formSchema>;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  organizationKey: string;
  selectedUserKey?: string;
  initialData?: ExternalUserFormData;
  mode: 'create' | 'edit';
}

function getDefaultNotifications(): NotificationConfigurationInput[] {
  return MESSAGE_TYPES.map((messageType) => ({
    messageType,
    activeChannels: [],
  }));
}

const styles = {
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '60vh',
    p: 0,
    position: 'relative',
  },
  scrollableContainer: {
    flex: 1,
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'grey.100',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'grey.400',
      borderRadius: '4px',
    },
  },
  tableContainer: {
    border: 1,
    borderColor: 'divider',
    borderRadius: 1,
    overflow: 'hidden',
  },
  tableHeader: {
    display: 'grid',
    alignItems: 'center',
    gap: 2,
    bgcolor: 'grey.50',
    px: 2,
    py: 0.75,
    borderBottom: 1,
    borderColor: 'divider',
  },
  tableRow: {
    display: 'grid',
    alignItems: 'center',
    gap: 2,
    px: 2,
    py: 0.75,
    borderColor: 'divider',
    '&:hover': {
      bgcolor: 'grey.25',
    },
  },
  notificationRow: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    alignItems: 'center',
    gap: 2,
    px: 2,
    py: 0.75,
    borderColor: 'divider',
    '&:hover': {
      bgcolor: 'grey.25',
    },
  },
  headerText: {
    variant: 'overline',
    color: 'text.secondary',
    fontWeight: 'medium',
  },
  labelText: {
    variant: 'body2',
    fontWeight: 'medium',
  },
  userSettingsRow: {
    display: 'grid',
    alignItems: 'center',
    gap: 2,
    px: 2,
    py: 0.75,
    borderBottom: 1,
    borderColor: 'divider',
    '&:hover': {
      bgcolor: 'grey.25',
    },
  },
  descriptionText: {
    variant: 'body2',
    color: 'text.secondary',
    mb: 2,
  },
} as const;

export function ExternalUserFormDialog({
  isOpen,
  onClose,
  organizationKey,
  selectedUserKey,
  initialData,
  mode,
}: Props) {
  const queryClient = useQueryClient();
  const scrollIndicator = useScrollIndicator();

  const handleApiError = (error: AxiosError) => {
    const apiError = handleAPIError(error);
    // Check for external user email already exists error (1406)
    if (
      apiError.apiStatusCode ===
      ERROR_CODE_MAPPING.organization_external_user_duplicate_email
    ) {
      setError('email', {
        type: 'manual',
        message: 'An external notification already exists for this email',
      });
      return;
    }
    // For all other errors, throw to reject the promise
    throw apiError;
  };

  const createMutation = useMutationWithGlobalErrorHandler({
    mutationFn: (parameters: CreateExternalUserParameters) =>
      createExternalUser(organizationKey, parameters),
    errorMessage: 'Something went wrong trying to add a notification email',
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['external-users', organizationKey],
      });
      handleClose();
    },
    onError: (error) => {
      if (isPlanLimitError(error)) {
        return;
      }

      handleApiError(error);
    },
  });

  const updateMutation = useMutationWithGlobalErrorHandler({
    mutationFn: ({
      userKey,
      parameters,
    }: {
      userKey: string;
      parameters: UpdateExternalUserParameters;
    }) => updateExternalUser(organizationKey, userKey, parameters),
    errorMessage:
      'Something went wrong trying to update the notification email',
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['external-users', organizationKey],
      });
      handleClose();
    },
    onError: (error: AxiosError) => {
      handleApiError(error);
    },
  });

  const activeMutation = mode === 'create' ? createMutation : updateMutation;
  const isLoading = activeMutation.isPending;
  const error = activeMutation.error;

  const { control, handleSubmit, reset, setError, clearErrors } =
    useForm<ExternalUserFormData>({
      mode: 'onSubmit',
      reValidateMode: 'onSubmit',
      resolver: zodResolver(formSchema),
      defaultValues: initialData || {
        email: '',
        notifications: getDefaultNotifications(),
      },
    });

  useEffect(() => {
    if (initialData && !error && !isLoading) {
      reset(initialData);
    }
  }, [initialData, reset, error, isLoading]);

  const handleClose = () => {
    reset();
    clearErrors();
    activeMutation.reset();
    onClose();
  };

  const handleClickScroll = () => {
    if (scrollIndicator.element === null) {
      return;
    }

    scrollIndicator.element.scrollTo({
      top: scrollIndicator.element.scrollHeight,
      behavior: 'smooth',
    });

    scrollIndicator.hide();
  };

  const handleValidSubmission: SubmitHandler<ExternalUserFormData> = (data) => {
    activeMutation.reset();

    const label = data.email;

    if (mode === 'create') {
      createMutation.mutate({
        label,
        email: data.email,
        notifications: { updates: data.notifications },
      });
    } else if (mode === 'edit' && selectedUserKey) {
      updateMutation.mutate({
        userKey: selectedUserKey,
        parameters: {
          label,
          email: data.email,
          notifications: { updates: data.notifications },
        },
      });
    }
  };

  const dialogTitle =
    mode === 'create'
      ? 'Set up custom notification'
      : 'Edit custom notification';
  const submitButtonText = mode === 'create' ? 'Set up notification' : 'Save';

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit(handleValidSubmission)}>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent sx={styles.dialogContent}>
          <Box ref={scrollIndicator.ref} sx={styles.scrollableContainer}>
            <Stack spacing={3} sx={{ p: 3, pt: 1 }}>
              <Typography variant="body2">
                Enter the email address to receive notifications
              </Typography>
              <Box sx={{ width: '50%' }}>
                <FormTextField
                  fullWidth
                  name="email"
                  type="email"
                  label="Email address"
                  control={control}
                  disabled={isLoading}
                />
              </Box>
              <Typography variant="body2">
                Select which notifications should be sent
              </Typography>
              <Box>
                <Box sx={styles.tableContainer}>
                  <Box
                    sx={{
                      ...styles.tableHeader,
                      gridTemplateColumns: '1fr auto',
                    }}
                  >
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      fontWeight="medium"
                    >
                      Notification
                    </Typography>
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      fontWeight="medium"
                    >
                      Active
                    </Typography>
                  </Box>

                  {MESSAGE_TYPES.map((topic, topicIndex) => (
                    <Box
                      key={topic}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        alignItems: 'center',
                        gap: 2,
                        px: 2,
                        py: 0.75,
                        borderBottom:
                          topicIndex < MESSAGE_TYPES.length - 1 ? 1 : 0,
                        borderColor: 'divider',
                        '&:hover': {
                          bgcolor: 'grey.25',
                        },
                      }}
                    >
                      <Stack spacing={0.5}>
                        <Typography variant="body2" fontWeight="medium">
                          {MESSAGE_TYPE_LABELS[topic]}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {MESSAGE_TYPE_DESCRIPTIONS[topic]}
                        </Typography>
                      </Stack>

                      <Controller
                        name={`notifications.${topicIndex}.activeChannels`}
                        control={control}
                        render={({ field }) => {
                          const isChecked = field.value.includes('email');

                          const handleChange = (
                            _: unknown,
                            checked: boolean
                          ) => {
                            if (checked) {
                              field.onChange(['email']);
                            } else {
                              field.onChange([]);
                            }
                          };

                          return (
                            <Switch
                              size="small"
                              checked={isChecked}
                              onChange={handleChange}
                              disabled={isLoading}
                            />
                          );
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            </Stack>
          </Box>
          <ScrollIndicator
            label="More"
            isVisible={scrollIndicator.isVisible}
            onClick={handleClickScroll}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {isLoading ? <CircularProgress size={20} /> : submitButtonText}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
